class MomentoHeader extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.abortController = new AbortController();
    this.compact = false;
    this.frame = 0;
    this.lastFocusedElement = null;
    this.mobilePdp = this.dataset.mobilePdp === 'true';
    this.mobilePdpQuery = window.matchMedia('(max-width: 680px)');
    this.pdpLastScrollY = Math.max(window.scrollY, 0);
    this.pdpDirectionDistance = 0;
    this.pdpDirection = 0;
    this.pdpStickyState = 'idle';
    this.pdpHasExitedTopStack = false;

    this.topStack = this.querySelector('[data-momento-top-stack]');
    this.siteHeader = this.querySelector('.site-header');
    this.mobileInventoryBanner = document.querySelector('.mobile-inventory-banner');
    this.drawerLayer = this.querySelector('[data-momento-menu-layer]');
    this.drawer = this.querySelector('[data-momento-menu-drawer]');
    this.openButton = this.querySelector('[data-momento-menu-open]');
    this.closeButtons = this.querySelectorAll('[data-momento-menu-close]');

    const { signal } = this.abortController;
    this.openButton?.addEventListener('click', () => this.openMenu(), { signal });
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.closeMenu(), { signal }));
    this.drawer?.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => this.closeMenu(), { signal }));
    this.drawer?.querySelectorAll('[data-localization-select]').forEach((select) => {
      select.addEventListener('change', () => select.form?.submit(), { signal });
    });
    document.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
    window.addEventListener('scroll', () => this.scheduleHeaderUpdate(), { passive: true, signal });
    window.addEventListener('resize', () => this.scheduleHeaderUpdate(), { signal });
    this.updateHeader();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    if (this.frame) cancelAnimationFrame(this.frame);
    if (!this.drawerLayer?.hidden) this.unlockPageScroll();
    this.initialized = false;
  }

  scheduleHeaderUpdate() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => this.updateHeader());
  }

  updateHeader() {
    this.frame = 0;
    const nextCompact = this.compact ? window.scrollY > 36 : window.scrollY >= 96;
    if (nextCompact !== this.compact) {
      this.compact = nextCompact;
      this.topStack?.classList.toggle('site-top-stack--compact', nextCompact);
      this.topStack?.setAttribute('data-header-state', nextCompact ? 'compact' : 'expanded');
      this.topStack?.setAttribute('data-header-theme', nextCompact ? 'dark' : 'light');
    }

    this.updateMobilePdpHeader();
  }

  updateMobilePdpHeader() {
    if (!this.mobilePdp || !this.siteHeader) return;

    const scrollY = Math.max(window.scrollY, 0);
    if (!this.mobilePdpQuery.matches) {
      this.resetMobilePdpHeader(scrollY);
      return;
    }

    if (scrollY <= 1) {
      this.resetMobilePdpHeader(scrollY);
      return;
    }

    const bannerBottom = this.mobileInventoryBanner
      ? this.mobileInventoryBanner.getBoundingClientRect().bottom + scrollY
      : 0;

    if (!this.pdpHasExitedTopStack) {
      this.pdpLastScrollY = scrollY;
      if (scrollY <= bannerBottom) return;
      this.pdpHasExitedTopStack = true;
      this.setMobilePdpStickyState('hidden', false);
      return;
    }

    const delta = scrollY - this.pdpLastScrollY;
    this.pdpLastScrollY = scrollY;
    if (Math.abs(delta) < 0.5) return;

    const direction = delta > 0 ? 1 : -1;
    if (direction !== this.pdpDirection) {
      this.pdpDirection = direction;
      this.pdpDirectionDistance = 0;
    }
    this.pdpDirectionDistance += Math.abs(delta);
    if (this.pdpDirectionDistance < 10) return;

    this.setMobilePdpStickyState(direction > 0 ? 'hidden' : 'visible');
    this.pdpDirectionDistance = 0;
  }

  setMobilePdpStickyState(state, animate = true) {
    if (!this.siteHeader || state === this.pdpStickyState) return;
    this.pdpStickyState = state;
    this.siteHeader.dataset.pdpStickyState = state;
    if (animate) this.siteHeader.dataset.pdpStickyAnimated = 'true';
  }

  resetMobilePdpHeader(scrollY = Math.max(window.scrollY, 0)) {
    this.pdpLastScrollY = scrollY;
    this.pdpDirectionDistance = 0;
    this.pdpDirection = 0;
    this.pdpStickyState = 'idle';
    this.pdpHasExitedTopStack = false;
    this.siteHeader?.removeAttribute('data-pdp-sticky-state');
    this.siteHeader?.removeAttribute('data-pdp-sticky-animated');
  }

  openMenu() {
    if (!this.drawerLayer || !this.drawer || !this.drawerLayer.hidden) return;
    this.lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.lockedScrollY = window.scrollY;
    document.body.style.setProperty('--momento-drawer-scroll-offset', `-${this.lockedScrollY}px`);
    this.drawerLayer.hidden = false;
    document.body.classList.add('momento-drawer-open');
    this.openButton?.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => this.drawer.focus({ preventScroll: true }));
  }

  closeMenu() {
    if (!this.drawerLayer || this.drawerLayer.hidden) return;
    this.drawerLayer.hidden = true;
    this.unlockPageScroll();
    this.openButton?.setAttribute('aria-expanded', 'false');
    this.lastFocusedElement?.focus();
  }

  unlockPageScroll() {
    const scrollY = this.lockedScrollY || 0;
    document.body.classList.remove('momento-drawer-open');
    document.body.style.removeProperty('--momento-drawer-scroll-offset');
    window.scrollTo(0, scrollY);
  }

  handleKeydown(event) {
    if (!this.drawerLayer || this.drawerLayer.hidden || !this.drawer) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeMenu();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = [...this.drawer.querySelectorAll('button, a, select, input')]
      .filter((element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true');
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (document.activeElement === this.drawer) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('momento-header')) {
  customElements.define('momento-header', MomentoHeader);
}
